import * as THREE from "three";
import { FLORA_SHARED } from "./material";
import { matureFloraMaterials } from './materialPool';

/** Bounds include both geometry levels and a margin for shader wind. */
export function prepareScatterBounds(mesh: THREE.InstancedMesh, coarse?: THREE.BufferGeometry): void {
  const detailed = mesh.geometry;
  mesh.computeBoundingSphere();
  if (coarse) {
    const bounds = mesh.boundingSphere!.clone();
    mesh.geometry = coarse;
    mesh.computeBoundingSphere();
    mesh.boundingSphere!.union(bounds);
    mesh.geometry = detailed;
    mesh.userData.scatterDetailed = detailed;
    mesh.userData.scatterCoarse = coarse;
    mesh.userData.scatterDetailedShadow = mesh.castShadow;
  }
  mesh.boundingSphere!.radius += 8;
  // Big terrain tiles span well beyond small plants' fade distances. Whole-tile
  // bounds alone still sent thousands of fully invisible plants to every pass.
  if ((mesh.userData.alpineCover || mesh.userData.meadowCover || mesh.userData.forestFloor || mesh.userData.wetlandCover || mesh.userData.coastalCover) && mesh.userData.fadeFar > 0) {
    mesh.userData.coverCull = { matrices: mesh.instanceMatrix.array.slice(), colors: mesh.instanceColor?.array.slice(),
      eye: new THREE.Vector3(Infinity, Infinity, Infinity), scale: -1, count: mesh.count };
  }
}

/** Terrain traversal applies batch LOD and amortized ground-cover compaction. */
export function updateScatterLod(mesh: THREE.InstancedMesh, eye: THREE.Vector3, visible: boolean): void {
  matureFloraMaterials(mesh);
  if (mesh.userData.blades && mesh.geometry.boundingSphere) {
    const b = mesh.geometry.boundingSphere, c = b.center, p = mesh.position;
    const nearest = Math.max(0, Math.hypot(eye.x - p.x - c.x, eye.y - p.y - c.y, eye.z - p.z - c.z) - b.radius);
    mesh.visible = visible && nearest <= mesh.userData.fadeFar * FLORA_SHARED.FADE_SCALE.value;
    return;
  }
  const sphere = mesh.boundingSphere;
  if (!sphere || !visible) { mesh.visible = visible; return; }
  const distance = eye.distanceTo(sphere.center);
  const nearest = Math.max(0, distance - sphere.radius);
  const scale = FLORA_SHARED.FADE_SCALE.value;
  const far = mesh.userData.fadeFar ?? 0;
  const near = mesh.userData.fadeNear ?? 0;
  // Cull only when the entire padded batch is outside the shader fade band.
  mesh.visible = !(far > 0 && nearest > far * scale)
    && !(near > 0 && distance + sphere.radius < (near - 150) * scale);
  const cover = mesh.userData.coverCull;
  if (mesh.visible && cover && (cover.scale !== scale || cover.eye.distanceToSquared(eye) > 4)) {
    const source = cover.matrices as Float32Array, target = mesh.instanceMatrix.array;
    // Includes plant extent, wind and the 2 m camera movement between updates.
    const limit2 = (far * scale + 10) ** 2;
    let count = 0;
    for (let i = 0; i < cover.count; i++) {
      const k = i * 16;
      const dx = source[k + 12] - eye.x, dy = source[k + 13] - eye.y, dz = source[k + 14] - eye.z;
      if (dx * dx + dy * dy + dz * dz > limit2) continue;
      for (let j = 0; j < 16; j++) target[count * 16 + j] = source[k + j];
      if (cover.colors && mesh.instanceColor) for (let j = 0; j < 3; j++) mesh.instanceColor.array[count * 3 + j] = cover.colors[i * 3 + j];
      count++;
    }
    mesh.count = count;
    mesh.instanceMatrix.clearUpdateRanges();
    if (count) mesh.instanceMatrix.addUpdateRange(0, count * 16);
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) {
      mesh.instanceColor.clearUpdateRanges();
      if (count) mesh.instanceColor.addUpdateRange(0, count * 3);
      mesh.instanceColor.needsUpdate = true;
    }
    cover.eye.copy(eye); cover.scale = scale;
  }
  const coarse = mesh.userData.scatterCoarse as THREE.BufferGeometry | undefined;
  if (coarse) {
    const detailed = mesh.userData.scatterDetailed as THREE.BufferGeometry;
    const threshold = (mesh.geometry === coarse ? (mesh.userData.scatterLodNear??280) : (mesh.userData.scatterLodFar??340)) * scale;
    mesh.geometry = nearest > threshold ? coarse : detailed;
    mesh.castShadow = mesh.geometry === detailed && mesh.userData.scatterDetailedShadow !== false;
  }
}
